import type { HoursStatus } from '../branding/hours-status.js';
import { WASH_MILESTONES } from '../milestones/fanout.js';
import type { PromotionEffect } from '../schemas/promotion.js';
import type { I18nString } from '../schemas/primitives.js';
import {
  ATTRIBUTION_CAMPAIGN_MAX,
  AttributionCampaignSchema,
  AttributionSourceSchema,
  type AttributionSource,
  type TicketAttribution,
} from '../schemas/ticket.js';

/**
 * Share & grow — the pure half of the operator Share kit and of booking
 * attribution (feature doc `docs/features/share-and-grow.feature.md`).
 *
 * Everything here is strings and numbers in, strings and data out: no DOM, no
 * Firestore, no clock. The dashboard renders what `deriveShareMoments` returns,
 * the customer app parses what `parseAttribution` accepts, and `bookTicket`
 * trusts nothing it was not able to parse with the same schema.
 *
 * The one rule this module exists to keep: **a moment is never invented.** Each
 * one is derived from a record the operator already has — an active promotion,
 * a wash count the lifetime counter actually reached, today's real hours, a
 * real five-star review. If the data is not there, the moment is not offered.
 */

// ── Attribution ────────────────────────────────────────────────────────────

/** URL parameter names a shared link carries. Short because they end up in chat bubbles. */
export const ATTRIBUTION_SOURCE_PARAM = 'src';
export const ATTRIBUTION_CAMPAIGN_PARAM = 'c';

/**
 * Read `?src=` / `?c=` into a validated attribution, or `null`.
 *
 * Lenient about case and whitespace (a link retyped by hand), strict about
 * vocabulary: an unknown source yields `null` rather than `other`, because
 * "other" is a real answer and guessing it would inflate a bucket the operator
 * reads as evidence. A malformed campaign is dropped and the source kept — the
 * channel is still true even if the moment id was mangled in transit.
 */
export function parseAttribution(
  source: string | null | undefined,
  campaign: string | null | undefined,
): TicketAttribution | null {
  const parsedSource = AttributionSourceSchema.safeParse(
    typeof source === 'string' ? source.trim().toLowerCase() : source,
  );
  if (!parsedSource.success) return null;
  const parsedCampaign = AttributionCampaignSchema.safeParse(
    typeof campaign === 'string' ? campaign.trim().toLowerCase() : campaign,
  );
  return parsedCampaign.success
    ? { source: parsedSource.data, campaign: parsedCampaign.data }
    : { source: parsedSource.data };
}

/** Turn any id into an attribution-safe campaign slug (≤ 64, `[a-z0-9_-]`). */
export function campaignSlug(raw: string): string {
  const slug = raw
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^[-_]+|[-_]+$/g, '')
    .slice(0, ATTRIBUTION_CAMPAIGN_MAX)
    .replace(/[-_]+$/g, '');
  return slug.length > 0 ? slug : 'share';
}

// ── Links ──────────────────────────────────────────────────────────────────

/** Campaign id stamped on every customer-to-customer referral share. */
export const REFERRAL_SHARE_CAMPAIGN = 'referral';

/** Path under the tenant's app that serves the rich-preview page for a shared link. */
export const SHARE_LANDING_PATH = 'share';

/**
 * The trackable link for one moment on one channel.
 *
 * `appBaseUrl` is the tenant's customer-app URL as `customerAppUrl` builds it
 * (`https://kilatcarwash.net/{slug}/`). The link points at `/{slug}/share`,
 * which in production is a tiny page carrying the operator's Open Graph tags
 * (so a Facebook or WhatsApp preview shows THEIR name and icon) that forwards
 * to the app home with the same query; in the SPA the route simply redirects.
 */
export function buildShareLink(
  appBaseUrl: string,
  source: AttributionSource,
  campaign?: string,
  extra?: { readonly ref?: string },
): string {
  const base = appBaseUrl.endsWith('/') ? appBaseUrl : `${appBaseUrl}/`;
  const params = new URLSearchParams();
  if (extra?.ref) params.set('ref', extra.ref);
  params.set(ATTRIBUTION_SOURCE_PARAM, source);
  if (campaign) params.set(ATTRIBUTION_CAMPAIGN_PARAM, campaignSlug(campaign));
  return `${base}${SHARE_LANDING_PATH}?${params.toString()}`;
}

/** Channels the Share kit offers a button for. */
export const SHARE_CHANNELS = [
  'whatsapp',
  'facebook',
  'x',
  'telegram',
  'instagram',
  'tiktok',
] as const satisfies readonly AttributionSource[];
export type ShareChannel = (typeof SHARE_CHANNELS)[number];

/**
 * Whether a channel can take the caption pre-filled from a web link.
 *
 * Facebook cannot: `sharer.php` ignores any text (Meta Platform Policy 2.3; the
 * old `quote` parameter is deprecated), so the caption has to travel by
 * clipboard. Instagram and TikTok have no web share URL at all.
 */
export function channelPrefillsText(channel: ShareChannel): boolean {
  return channel === 'whatsapp' || channel === 'x' || channel === 'telegram';
}

/**
 * The web-intent URL that opens a channel's composer, or `null` for a channel
 * with no such URL (Instagram, TikTok: download the image and post in the app).
 */
export function shareIntentUrl(
  channel: ShareChannel,
  message: { readonly text: string; readonly url: string },
): string | null {
  const text = encodeURIComponent(message.text);
  const url = encodeURIComponent(message.url);
  switch (channel) {
    case 'whatsapp':
      // wa.me has no url field — the link goes in the body, on its own line so
      // WhatsApp builds a preview card from it.
      return `https://wa.me/?text=${encodeURIComponent(`${message.text}\n\n${message.url}`)}`;
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    case 'x':
      return `https://x.com/intent/post?text=${text}&url=${url}`;
    case 'telegram':
      return `https://t.me/share/url?url=${url}&text=${text}`;
    case 'instagram':
    case 'tiktok':
      return null;
  }
}

// ── Moments ────────────────────────────────────────────────────────────────

/** What a promotion actually gives, in a shape the caption can phrase per locale. */
export type ShareOffer =
  | { readonly kind: 'pctOff'; readonly pct: number }
  | { readonly kind: 'fixedOff'; readonly sen: number }
  | { readonly kind: 'newPrice'; readonly sen: number }
  | { readonly kind: 'bonusPoints'; readonly points: number }
  | { readonly kind: 'freeExtra' };

export function offerFromEffect(effect: PromotionEffect): ShareOffer {
  switch (effect.type) {
    case 'pct_off':
      return { kind: 'pctOff', pct: effect.value };
    case 'fixed_off':
      return { kind: 'fixedOff', sen: effect.value };
    case 'replace_price':
      return { kind: 'newPrice', sen: effect.newPrice };
    case 'bonus_points':
      return { kind: 'bonusPoints', points: effect.points };
    case 'free_service':
    case 'free_add_on':
      return { kind: 'freeExtra' };
  }
}

export type ShareMoment =
  | {
      readonly kind: 'promotion';
      readonly id: string;
      readonly promotionId: string;
      readonly name: I18nString;
      readonly offer: ShareOffer;
      readonly endsAtMs: number;
    }
  | { readonly kind: 'milestone'; readonly id: string; readonly washes: number }
  | { readonly kind: 'openNow'; readonly id: string; readonly closesAt: string | null }
  | {
      readonly kind: 'quietNow';
      readonly id: string;
      readonly waiting: number;
      readonly closesAt: string | null;
    }
  | { readonly kind: 'referral'; readonly id: string }
  | { readonly kind: 'review'; readonly id: string; readonly text: string };

export type ShareMomentKind = ShareMoment['kind'];

/** The structural minimum of a promotion this module reads. */
export interface ShareMomentPromotion {
  readonly id: string;
  readonly name: I18nString;
  readonly status: 'draft' | 'active' | 'paused' | 'archived';
  readonly validFromMs: number;
  readonly validToMs: number;
  readonly effect: PromotionEffect;
}

/** The structural minimum of a review. Deliberately has NO author field. */
export interface ShareMomentReview {
  readonly rating: number;
  readonly text?: string | undefined;
  readonly hidden: boolean;
  readonly createdAtMs: number;
}

export interface ShareMomentInput {
  readonly nowMs: number;
  readonly promotions: readonly ShareMomentPromotion[];
  /** `tenants/{tid}/stats/lifetime.washCount`; absent when the counter has never moved. */
  readonly washCount?: number | undefined;
  /** Today's status from `computeHoursStatus`, or `null` when hours are not loaded. */
  readonly hoursStatus: HoursStatus | null;
  /** Cars waiting in the live queue right now, or `null` when not known. */
  readonly waitingCount: number | null;
  readonly referralsEnabled: boolean;
  readonly reviews: readonly ShareMomentReview[];
}

/** At most this many promotion moments — a wall of near-identical cards helps nobody choose. */
export const SHARE_MAX_PROMOTIONS = 5;
/** "Quiet right now" only when this many or fewer cars are waiting. */
export const SHARE_QUIET_MAX_WAITING = 1;
/** Below this the "milestone" is the first wash, which is not something to post about. */
export const SHARE_MIN_MILESTONE = 10;
/** A five-star review shorter than this is "ok" or "👍" — nothing a stranger would read. */
export const SHARE_MIN_REVIEW_CHARS = 12;

/**
 * Every moment the operator can share right now, in the order worth reading:
 * live offers first, then the shop's status today, then proof (milestone,
 * review), then the referral programme.
 */
export function deriveShareMoments(input: ShareMomentInput): ShareMoment[] {
  const moments: ShareMoment[] = [];

  const livePromotions = input.promotions
    .filter(
      (p) => p.status === 'active' && p.validFromMs <= input.nowMs && input.nowMs <= p.validToMs,
    )
    // Ending soonest first: the offer that is about to lapse is the one to post.
    .sort((a, b) => a.validToMs - b.validToMs)
    .slice(0, SHARE_MAX_PROMOTIONS);
  for (const p of livePromotions) {
    moments.push({
      kind: 'promotion',
      id: campaignSlug(`promo-${p.id}`),
      promotionId: p.id,
      name: p.name,
      offer: offerFromEffect(p.effect),
      endsAtMs: p.validToMs,
    });
  }

  const status = input.hoursStatus;
  const closesAt = status?.kind === 'open' || status?.kind === 'holiday_open' ? status.until : null;
  const isOpen =
    status?.kind === 'open' || status?.kind === 'holiday_open' || status?.kind === 'open_24h';
  if (isOpen) {
    if (input.waitingCount !== null && input.waitingCount <= SHARE_QUIET_MAX_WAITING) {
      moments.push({ kind: 'quietNow', id: 'quiet-now', waiting: input.waitingCount, closesAt });
    } else {
      moments.push({ kind: 'openNow', id: 'open-now', closesAt });
    }
  }

  const washes = input.washCount ?? 0;
  const reached = WASH_MILESTONES.filter((m) => m >= SHARE_MIN_MILESTONE && m <= washes);
  const milestone = reached[reached.length - 1];
  if (milestone !== undefined) {
    moments.push({ kind: 'milestone', id: `milestone-${milestone}`, washes: milestone });
  }

  const review = [...input.reviews]
    .filter(
      (r) => !r.hidden && r.rating === 5 && (r.text ?? '').trim().length >= SHARE_MIN_REVIEW_CHARS,
    )
    .sort((a, b) => b.createdAtMs - a.createdAtMs)[0];
  if (review) {
    moments.push({ kind: 'review', id: 'review-5-star', text: (review.text ?? '').trim() });
  }

  if (input.referralsEnabled) moments.push({ kind: 'referral', id: REFERRAL_SHARE_CAMPAIGN });

  return moments;
}
